import React, { useState, useRef, useEffect } from 'react';
import './MobileFormField.css';

const MobileFormField = ({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  required = false, 
  error, 
  disabled = false,
  options = [],
  icon: Icon,
  helpText,
  autoComplete,
  autoFocus = false,
  maxLength,
  pattern,
  inputMode
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setHasValue(value !== undefined && value !== null && value !== '');
  }, [value]);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setHasValue(newValue !== '');
    onChange?.(newValue);
  };

  const fieldId = `field-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  const renderInput = () => {
    const commonProps = {
      id: fieldId,
      value: value || '',
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      required,
      disabled,
      ref: inputRef,
      autoComplete,
      maxLength,
      pattern,
      'aria-invalid': error ? 'true' : 'false',
      'aria-describedby': error ? `${fieldId}-error` : helpText ? `${fieldId}-help` : undefined
    };

    if (type === 'select') {
      return (
        <select 
          {...commonProps}
          className={`mobile-field-select ${isFocused ? 'focused' : ''} ${hasValue ? 'has-value' : ''} ${error ? 'error' : ''}`}
        >
          <option value="" disabled>{placeholder || `Select ${label}`}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'textarea') {
      return (
        <textarea
          {...commonProps}
          placeholder={placeholder}
          inputMode={inputMode}
          className={`mobile-field-textarea ${isFocused ? 'focused' : ''} ${hasValue ? 'has-value' : ''} ${error ? 'error' : ''}`}
          rows={4}
        />
      );
    }

    return (
      <input
        {...commonProps}
        type={type}
        placeholder={placeholder}
        inputMode={inputMode}
        className={`mobile-field-input ${isFocused ? 'focused' : ''} ${hasValue ? 'has-value' : ''} ${error ? 'error' : ''}`}
      />
    );
  };

  return (
    <div className={`mobile-form-field ${isFocused ? 'focused' : ''} ${hasValue ? 'has-value' : ''} ${error ? 'has-error' : ''} ${disabled ? 'disabled' : ''}`}>
      {Icon && (
        <div className="mobile-field-icon">
          <Icon size={20} />
        </div>
      )}
      
      <div className="mobile-field-wrapper">
        {/* Floating label */}
        {label && (
          <label 
            htmlFor={fieldId}
            className={`mobile-field-label ${required ? 'required' : ''}`}
          >
            {label}
          </label>
        )}
        
        {/* Input field */}
        {renderInput()}
        
        {/* Focus underline */}
        <div className="mobile-field-underline"></div>
        
        {/* Help text */}
        {helpText && !error && (
          <p id={`${fieldId}-help`} className="mobile-field-help">
            {helpText}
          </p>
        )}
        
        {/* Error message */}
        {error && (
          <p id={`${fieldId}-error`} className="mobile-field-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default MobileFormField;
